import { getAccountStore, getRulesStore } from '../storage/stores';
import { TrackingData, processUrlResponse } from '../types';
import { getApi } from '../api';
import { getClassification } from './apiActions';
import { trackEvent } from './trackingActions';
import { getLinewizeBlockPagesHosts, TAMPERING_EVENT_NAME } from '../constants';
import { validateClassification } from './engineActions';
import { ReportSiteAction, ReportSiteReason } from '../types/api/events';
import { getWhitelistVerdict } from '../validators/domainValidators';
import { setupApiAccount } from '../api/account';
import { ProcessedVerdict } from '../navigation/verdict';
import { isDelegationStarted } from '../delegation/delegation';
import { identifySentry } from '../lib/vendor/sentry';

export const initAccount = async () => {
  const api = getApi();
  const { data: me } = await api.me.get();
  const {
    id: accountId,
    uid: accountUid,
    master_account_id: masterAccountId,
    master_account_uid: masterAccountUid,
    locale,
  } = me;
  await getAccountStore().set({
    accountId,
    accountUid,
    masterAccountId,
    masterAccountUid,
    locale,
  });

  // Identify Sentry with the accountUID as soon as it's persisted to the storage
  identifySentry();

  setupApiAccount({ accountId, accountUid, masterAccountId, masterAccountUid });
};

export const processUrl = async (url: string): Promise<ProcessedVerdict> => {
  const rules = getRulesStore().get();
  if (!rules) {
    throw new Error('Rules store not properly initialized!');
  }

  const { hostname } = new URL(url);

  // Check if we should ignore the host
  if (isIgnoredHostname(hostname)) {
    return { verdict: processUrlResponse.ignore() };
  }

  const classification = await getClassification(hostname);

  const whitelistResults = getWhitelistVerdict(
    rules.domainRules,
    classification?.hostname || hostname
  );
  if (whitelistResults !== undefined) {
    // Report site except if it's ignored
    if (whitelistResults.action !== ReportSiteAction.IGNORE) {
      return {
        verdict: whitelistResults.verdict,
        webEvent: {
          url,
          hostname,
          categories: [],
          categories_match: [],
          action: whitelistResults.action,
          reason: ReportSiteReason.WHITELIST,
        },
      };
    }
    return { verdict: whitelistResults.verdict };
  }

  return Promise.resolve(
    validateClassification({ url, hostname, rules, classification })
  );
};

export const sendTamperingEvent = ({
  deviceUid,
  accountUid,
  profileId,
  profileName,
}: TrackingData) =>
  trackEvent({
    event: TAMPERING_EVENT_NAME,
    properties: {
      device_uid: deviceUid,
      profile_id: profileId,
      profile_name: profileName,
    },
    accountUid,
  });

/**
 * Return if the hostname should be ignored or not.
 *
 * Ignored:
 * - Linewize block pages domains (when delegation is active)
 */
const isIgnoredHostname = (hostname: string) => {
  return (
    isDelegationStarted() &&
    getLinewizeBlockPagesHosts().some(rx => RegExp(rx).test(hostname))
  );
};
